import type { ComponentProps } from "react";

import { memo, useImperativeHandle, useRef } from "react";

import { Spinner } from "@alaffia-technology-solutions/ds";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import { usePDFStore } from "../../providers";
import { usePDFViewerEvent } from "../../use-pdf-viewer-event";
import { usePDFViewerSync } from "../../use-pdf-viewer-sync";
import { PDFErrorMessage } from "./components/pdf-error-message";

type PDFViewerCanvasProps = ComponentProps<"div"> & {
  url: string;
  fileId: string;
  pageIndex?: number;
  onOpenFile?: () => void;
  onPageClick?: () => void;
  onPageChange?: (pageNumber: number) => void;
};

export const PDFViewerCanvas = memo(function PDFViewerCanvas({
  ref,
  ...props
}: PDFViewerCanvasProps) {
  const {
    url,
    fileId,
    pageIndex,
    className,
    children,
    onOpenFile,
    onPageClick,
    onPageChange,
  } = props;
  const elementRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(ref, () => {
    if (!elementRef.current) {
      throw new Error("elementRef is not set");
    }

    return elementRef.current;
  }, [elementRef]);

  const { data: pdfViewerCtrl } = usePDFStore((store) => ({
    state: store.state,
    error: store.error,
    events: store.events,
    setCurrentPageIndex: store.setCurrentPageIndex,
  }));

  const { events, error, state, setCurrentPageIndex } = pdfViewerCtrl ?? {};

  usePDFViewerSync({
    ref: elementRef,
    url,
    fileId,
    pageIndex,
  });

  usePDFViewerEvent(events?.pageNumberChange, (pageNumber: number) => {
    setCurrentPageIndex?.(pageNumber - 1);
    onPageChange?.(pageNumber);
  });

  usePDFViewerEvent(events?.tapPage, () => {
    onPageClick?.();
  });

  usePDFViewerEvent(events?.pdfViewerOpenSuccess, () => {
    onOpenFile?.();
  });

  const showLoader = state === "closed";

  return (
    <>
      {error ? (
        <PDFErrorMessage error={error} />
      ) : (
        <div
          className={cn(
            "af-flex af-h-full af-w-full af-min-w-0 af-overflow-auto",
            {
              "af-items-center af-justify-center af-bg-interface-accent":
                showLoader,
            },
            className,
          )}
        >
          {showLoader && <Spinner className="af-size-16" />}
          <div ref={elementRef} className={"af-relative"} />
        </div>
      )}
      {state === "opened" && children}
    </>
  );
});

PDFViewerCanvas.displayName = "PDFViewerCanvas";
